import { MessageCircle, Phone, MapPin, ArrowUpRight, ShieldCheck, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, CASES } from "@/lib/constants";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const WORKSHOP_BG =
  "https://images.pexels.com/photos/9028761/pexels-photo-9028761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const Hero = () => {
  const { t } = useLanguage();
  const featured = CASES[0];

  return (
    <section
      id="home"
      className="relative overflow-hidden pt-28 md:pt-32 pb-16 md:pb-24"
      data-testid="hero-section"
    >
      {/* Workshop backdrop with heavy dark overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.18]"
        style={{ backgroundImage: `url("${WORKSHOP_BG}")` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/95 via-[#0a0a0a]/80 to-[#0a0a0a]" />
      <div className="absolute inset-0 dx-grain opacity-60" />

      {/* Diagonal red racing stripe */}
      <div className="absolute top-0 bottom-0 right-[18%] w-[2px] bg-gradient-to-b from-transparent via-[#e10600]/40 to-transparent rotate-12 origin-top hidden lg:block" />
      <div className="absolute top-0 bottom-0 right-[16%] w-[1px] bg-gradient-to-b from-transparent via-[#e10600]/20 to-transparent rotate-12 origin-top hidden lg:block" />

      {/* Soft red glows */}
      <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#e10600]/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full bg-[#e10600]/5 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center z-[2]">
        {/* Copy column */}
        <div className="dx-fade-up lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/15 bg-black/40 backdrop-blur text-[10px] tracking-[0.3em] uppercase font-display font-bold text-white/85 mb-7"
            data-testid="hero-eyebrow"
          >
            <ChevronRight className="w-3 h-3 text-[#e10600]" strokeWidth={3} />
            {t.hero.eyebrow}
          </div>

          <h1
            className="dx-hero-title font-display font-black text-white leading-[0.95] tracking-[-0.04em] text-[40px] sm:text-[56px] lg:text-7xl xl:text-[80px]"
            data-testid="hero-title"
          >
            {t.hero.title}
          </h1>

          <div className="mt-6 flex items-start gap-4">
            <div className="hidden md:block w-1 h-16 bg-[#e10600] mt-1" />
            <p
              className="text-white/70 text-base md:text-lg leading-relaxed max-w-xl"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </p>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href={BRAND.whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 px-6 py-4 rounded-full bg-[#e10600] hover:bg-[#c20500] text-white font-bold transition shadow-xl shadow-red-900/40"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {t.common.sendPhotosOnWhatsApp}
              <ArrowUpRight className="w-4 h-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </a>
            <a
              href={BRAND.telLink}
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/20 hover:bg-white/5 text-white font-bold transition"
              data-testid="hero-call-btn"
            >
              <Phone className="w-5 h-5" />
              {t.common.callNow}
            </a>
            <a
              href={BRAND.mapsLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/10 text-white/85 hover:text-white hover:border-white/25 font-bold transition"
              data-testid="hero-maps-btn"
            >
              <MapPin className="w-5 h-5" />
              {t.common.getDirections}
            </a>
          </div>

          <div
            className="mt-9 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-white/55 font-display font-bold"
            data-testid="hero-since-pill"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#e10600]" />
            {t.hero.sincePill}
          </div>
        </div>

        {/* Live proof column */}
        <div className="relative dx-fade-up lg:col-span-5" style={{ animationDelay: "120ms" }}>
          <span className="absolute -top-3 start-4 z-[3] px-3 py-1 rounded-full bg-[#e10600] text-white text-[10px] tracking-[0.25em] uppercase font-display font-black shadow-lg shadow-red-900/40">
            {t.hero.proofLabel}
          </span>
          <div className="absolute -inset-3 rounded-[28px] border border-white/10 pointer-events-none" />
          <div className="absolute -inset-6 rounded-[32px] border border-white/5 pointer-events-none" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/60 dx-chrome p-[2px]">
            <div className="rounded-[14px] overflow-hidden">
              <BeforeAfterSlider
                beforeSrc={featured.before}
                afterSrc={featured.after}
                caseNumber={featured.id}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-white/50 font-display font-bold">
            <span>
              {t.gallery.case} {featured.id}
            </span>
            <span className="h-px flex-1 mx-4 bg-gradient-to-r from-white/5 via-white/15 to-transparent" />
            <span className="text-[#e10600]">PDR · KW</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
